import * as React from 'react';
import { isEmpty, isEqual } from 'lodash';

import CampaignPieWidget from '../campaign-pie-widget/CampaignPieWidget';
import PieChart from 'components/graphs/echarts/pie-chart/PieChart';
import ReportingStatisticsTableWithBenchmark from '../reporting-statistics-table-with-benchmark/ReportingStatisticsTableWithBenchmark';

import { getEngagementPieChartData, preparePieChartData } from 'utils/charts/pie-chart';
import { abbrNum } from 'utils/helper/common';
import { COLORS, getReportingPieChartStyles } from 'utils/reporting';
import { __t, applyTranslations } from 'utils/language';

import type {
  ChartFormatterParams,
  StatisticsCommon
} from 'types';
import type { ReportingStatisticTableWithBenchmarkItem } from '../../types/ReportingStatisticTableWithBenchmarkItem.type';
import { ReportingDeliverableTypes } from 'modules/reporting/types/ReportingMetaInfo.type';

applyTranslations(require('./campaign-engagement-widget.i18n.json'));

import './campaign-engagement-widget.scss';

type Props = {
  engagementTableItems: Array<ReportingStatisticTableWithBenchmarkItem>,
  totalReactions: number,
  engagementData: {
    [x: string]: number
  },
  campaignId: number,
  isLoading: boolean,
  taskType?: ReportingDeliverableTypes
};

const CampaignEngagementWidget = ({
  engagementTableItems,
  totalReactions,
  engagementData,
  campaignId,
  isLoading,
  taskType
}: Props): JSX.Element => {
  const pieChartColors: Array<string> = [];
  const data = Object.keys(engagementData).reduce<Array<StatisticsCommon>>((res, type) => {
    const color = COLORS[type]?.[taskType || ''] || COLORS[type]?.main || '';

    if (engagementData[type]) {
      res.push({
        name: type,
        label: type,
        absolute: engagementData[type],
        percentage: Math.round(engagementData[type] / totalReactions * 100),
        emphasis: { itemStyle: { color } }
      });

      pieChartColors.push(color);
    }

    return res;
  }, []);

  const handleFormatImpressions = (params: ChartFormatterParams): string => (
    `{value|${abbrNum(params.value?.metric, { decimal: 1 }, true)}}\n{name|${params.name}}`
  );

  const pieChartData = preparePieChartData(
    getEngagementPieChartData(data),
    'audience',
    {
      formatter: handleFormatImpressions,
      colors: pieChartColors,
      styles: getReportingPieChartStyles()
    }, true);

  const renderTable = ():JSX.Element => (
    <ReportingStatisticsTableWithBenchmark
      wrapperClass="campaign-engagement-widget__statistics-table"
      statisticsItems={engagementTableItems}
      campaignId={campaignId}
      isLoading={isLoading}
    />
  );

  const renderPieChart = (): JSX.Element => (
    <div className="campaign-engagement-widget__graph">
      <PieChart
        key={`campaign-engagement-pie-chart--${isEmpty(pieChartData)}`}
        id="campaign-engagement-pie-chart"
        options={pieChartData}
        hide={false}
      />
    </div>
  );

  return (
    <CampaignPieWidget
      title={__t('campaign_engagement_widget.total_reactions_title')}
      total={abbrNum(totalReactions, { decimal: 1 }, true)}
      table={renderTable()}
      content={renderPieChart()}
      tablePosition="static"
      isLoading={isLoading}
      isPieCentered={false}
    />
  );
};

export default React.memo(
  CampaignEngagementWidget,
  (prevProps, nextProps) => isEqual(prevProps, nextProps)
);
